import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Menu, Eye, CheckCircle, X, Ticket, Star,
  Phone, Mail, User, AlignLeft,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import SearchableDropdown from "../components/SearchableDropdown";
import TableHeaderFilter from "../components/TableHeaderfilter";
import { SERVER_URL } from "../config";

// ─── Tiny helpers ──────────────────────────────────────────────────────────────

const Badge = ({ status }) => {
  const map = {
    open: "bg-blue-100 text-blue-700",
    in_progress: "bg-yellow-100 text-yellow-700",
    resolved: "bg-green-100 text-green-700",
    closed: "bg-gray-200 text-gray-600",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${map[status] ?? "bg-gray-100 text-gray-500"}`}>
      {status?.replace("_", " ")}
    </span>
  );
};

const StarRating = ({ value }) => {
  if (!value) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <span className="flex justify-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={14}
          className={i <= value ? "text-yellow-400 fill-yellow-400" : "text-gray-300"} />
      ))}
    </span>
  );
};

// Modal wrapper
const Modal = ({ children, onClose, wide }) => (
  <div className="fixed inset-0 bg-black/50 flex justify-center items-start pt-10 z-50 overflow-y-auto">
    <div className={`bg-white rounded-xl shadow-2xl w-full mx-4 relative ${wide ? "max-w-2xl" : "max-w-lg"}`}>
      <button className="absolute right-3 top-3 text-gray-400 hover:text-gray-700" onClick={onClose}>
        <X size={20} />
      </button>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────

export default function TicketingPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState("all");

  // Modals
  const [showForm, setShowForm] = useState(false);
  const [closeModal, setCloseModal] = useState(null);
  const [viewTicket, setViewTicket] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    ticketId: [], customerName: [], createdBy: [], service: [], assignTo: []
  });

  // ── Read logged-in employee from localStorage ─────────────────────────────
  const loggedIn = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("employee") || "{}"); }
    catch { return {}; }
  }, []);

  const employeeId = loggedIn.employee_id ?? "";
  const employeeName = loggedIn.employee_name ?? "";

  // ── Form state ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    ProductType: "", TallySlNo: "", customerName: "", contact_no: "", emailId: "",
    contact_person: "", assigned_to: "", serviceType: "", description: "",
    priority: "medium"
  });
  const [closeRemarks, setCloseRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => { fetchAll(); }, [activeTab]);

  const fetchAll = async () => {
    const tabParam = activeTab === "open" ? "open" : activeTab === "closed" ? "closed" : "all";
    const [tRes, eRes] = await Promise.all([
      axios.get(`${SERVER_URL}/Rahul/get_tickets.php?employee_id=${employeeId}&status=${tabParam}`),
      axios.get(`${SERVER_URL}/Rahul/get_employee.php`)
    ]);
    setTickets(tRes.data.data ?? []);
    setEmployees(eRes.data ?? []);
  };

  // ── Options ───────────────────────────────────────────────────────────────
  const employeeOptions = employees.map(e => ({
    label: `${e.name} (${e.id})`,
    value: e.id
  }));

  const productOptions = [
    { label: "Tally Prime", value: "Tally Prime" },
    { label: "Cloud", value: "Cloud" },
    { label: "Customization", value: "Customization" },
  ];

  const supportOptions = [
    { label: "Installation", value: "Installation" },
    { label: "Complaint", value: "Complaint" },
    { label: "Maintenance", value: "Maintenance" },
    { label: "Other", value: "Other" },
  ];

  const priorityOptions = [
    { label: "Low", value: "low" },
    { label: "Medium", value: "medium" },
    { label: "High", value: "high" },
    { label: "Critical", value: "critical" },
  ];

  // ── Filtered tickets ──────────────────────────────────────────────────────
  const filteredTickets = useMemo(() => {
    return tickets.filter(t =>
      (!filters.ticketId.length || filters.ticketId.includes(t.ticket_id)) &&
      (!filters.customerName.length || filters.customerName.includes(t.customerName)) &&
      (!filters.createdBy.length || filters.createdBy.includes(t.created_by)) &&
      (!filters.service.length || filters.service.includes(t.serviceType)) &&
      (!filters.assignTo.length || filters.assignTo.includes(t.assigned_to))
    );
  }, [tickets, filters]);

  // ── Generate ticket ───────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.customerName || !form.contact_no) {
      alert("Customer Name and Contact No. are required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/generate_ticket.php`, {
        ...form,
        created_by: employeeId
      });
      if (res.data.success) {
        alert(`✅ Ticket Generated!\nTicket ID: ${res.data.ticket_id}\nSMS Sent: ${res.data.sms_sent ? "Yes" : "No"}`);
        setShowForm(false);
        setForm({ customerName: "", contact_no: "", emailId: "", contact_person: "", assigned_to: "", serviceType: "", description: "", priority: "medium" });
        fetchAll();
      } else {
        alert("Error: " + res.data.message);
      }
    } catch (e) {
      alert("Request failed: " + e.message);
    }
    setSubmitting(false);
  };

  // ── Close ticket ──────────────────────────────────────────────────────────
  const handleClose = async () => {
    if (!closeModal) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/update_ticket.php`, {
        ticket_id: closeModal.ticket_id,
        remarks: closeRemarks
      });
      if (res.data.success) {
        alert(`✅ Ticket closed.\nResolution email sent: ${res.data.email_sent ? "Yes" : "No"}`);
        setCloseModal(null);
        setCloseRemarks("");
        fetchAll();
      } else {
        alert("Error: " + res.data.message);
      }
    } catch (e) {
      alert("Request failed: " + e.message);
    }
    setSubmitting(false);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen bg-gray-100 font-sans">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Top bar */}
        <div className="bg-white border-b px-5 py-3 flex justify-between items-center shadow-sm shrink-0">
          <div className="flex items-center gap-3">
            <Menu onClick={() => setSidebarOpen(true)} className="cursor-pointer text-gray-600" />
            <div className="flex items-center gap-2 text-indigo-800 font-bold text-lg">
              <Ticket size={20} /> Ticketing Management
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowForm(true)}
              className="bg-indigo-700 hover:bg-indigo-800 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 transition"
            >
              <Ticket size={14} /> Generate Ticket
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-4 pb-2 flex gap-2 shrink-0">
          {["all", "open", "closed"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition capitalize
                ${activeTab === tab
                  ? "bg-indigo-700 text-white shadow"
                  : "bg-white border text-gray-600 hover:bg-indigo-50"}`}
            >
              {tab}
            </button>
          ))}
          <span className="ml-auto text-xs text-gray-400 self-center">{filteredTickets.length} record(s)</span>
        </div>

        {/* Table container — fills remaining height, clips overflow */}
        <div className="flex-1 overflow-hidden px-5 pb-4">
          <div className="bg-white rounded-xl shadow h-full flex flex-col overflow-hidden">
            <div className="overflow-auto flex-1">
              <table className="w-full text-sm border-collapse">

                {/* ── Sticky header ── */}
                <thead className="bg-indigo-900 text-white sticky top-0 z-10">
                  <tr>
                    <th className="p-2 text-left">
                      <TableHeaderFilter
                        label="Ticket ID"
                        // Only show IDs present in the current ticket list
                        options={[...new Set(tickets.map(t => t.ticket_id))].map(id => ({ label: id, value: id }))}
                        selectedValues={filters.ticketId}
                        onChange={v => setFilters(f => ({ ...f, ticketId: v }))}
                      />
                    </th>
                    <th className="p-2 text-left whitespace-nowrap text-slate-500 text-xs font-bold uppercase">
                      Generated Date
                    </th>
                    <th className="p-2 text-left">
                      <TableHeaderFilter
                        label="Customer"
                        // Already dynamic, but ensured uniqueness
                        options={[...new Set(tickets.map(t => t.customerName))].map(c => ({ label: c, value: c }))}
                        selectedValues={filters.customerName}
                        onChange={v => setFilters(f => ({ ...f, customerName: v }))}
                      />
                    </th>
                    <th className="p-2 text-left">
                      <TableHeaderFilter
                        label="Created By"
                        // Dynamically extract only those who have actually created a ticket in this list
                        options={[...new Set(tickets.map(t => t.created_by_name))].filter(Boolean).map(name => {
                          const ticket = tickets.find(t => t.created_by_name === name);
                          return { label: name, value: ticket.created_by };
                        })}
                        selectedValues={filters.createdBy}
                        onChange={v => setFilters(f => ({ ...f, createdBy: v }))}
                      />
                    </th>
                    <th className="p-2 text-left">
                      <TableHeaderFilter
                        label="Service"
                        // Only show service types currently present in the table
                        options={[...new Set(tickets.map(t => t.serviceType))].filter(Boolean).map(s => ({ label: s, value: s }))}
                        selectedValues={filters.service}
                        onChange={v => setFilters(f => ({ ...f, service: v }))}
                      />
                    </th>
                    <th className="p-2 text-left">
                      <TableHeaderFilter
                        label="Assigned To"
                        // Only show employees who are actually assigned to tickets in this list
                        options={[...new Set(tickets.map(t => t.assigned_person))].filter(Boolean).map(name => {
                          const ticket = tickets.find(t => t.assigned_person === name);
                          return { label: name, value: ticket.assigned_to };
                        })}
                        selectedValues={filters.assignTo}
                        onChange={v => setFilters(f => ({ ...f, assignTo: v }))}
                      />
                    </th>
                    <th className="p-2 text-left text-slate-500 text-xs font-bold uppercase whitespace-nowrap">Status</th>
                    <th className="p-2 text-left text-slate-500 text-xs font-bold uppercase whitespace-nowrap">Closed Date</th>
                    <th className="p-2 text-center text-slate-500 text-xs font-bold uppercase">Feedback</th>
                    <th className="p-2 text-center text-slate-500 text-xs font-bold uppercase">Action</th>
                  </tr>
                </thead>

                {/* ── Scrollable body ── */}
                <tbody>
                  {filteredTickets.length === 0 && (
                    <tr>
                      <td colSpan={10} className="text-center py-10 text-gray-400">No tickets found</td>
                    </tr>
                  )}
                  {filteredTickets.map(t => (
                    <tr key={t.ticket_id} className="border-t hover:bg-indigo-50 transition">
                      <td className="p-2 font-mono text-xs text-indigo-700">{t.ticket_id}</td>
                      <td className="p-2 whitespace-nowrap text-gray-500 text-xs">
                        {t.generated_date
                          ? new Date(t.generated_date).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true
                          })
                          : "—"}
                      </td>
                      <td className="p-2">{t.customerName}</td>
                      <td className="p-2 text-gray-600">{t.created_by_name}</td>
                      <td className="p-2">{t.serviceType}</td>
                      <td className="p-2">{t.assigned_person ?? "—"}</td>
                      <td className="p-2"><Badge status={t.status} /></td>
                      {/* ── Updated Closed Date Column ── */}
                      <td className="p-2 text-xs text-gray-500 whitespace-nowrap">
                        {t.status === "closed" && t.closed_date
                          ? new Date(t.closed_date).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true
                          })
                          : "—"}
                      </td>
                      <td className="p-2 text-center"><StarRating value={t.feedback} /></td>
                      <td className="p-2">
                        <div className="flex justify-center gap-2">
                          <button
                            title="View"
                            onClick={() => setViewTicket(t)}
                            className="p-1.5 rounded-lg bg-indigo-100 hover:bg-indigo-200 text-indigo-700"
                          >
                            <Eye size={14} />
                          </button>
                          {t.status !== "closed" && (
                            <button
                              title="Close Ticket"
                              onClick={() => { setCloseModal(t); setCloseRemarks(""); }}
                              className="p-1.5 rounded-lg bg-green-100 hover:bg-green-200 text-green-700"
                            >
                              <CheckCircle size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Generate Ticket Modal ─────────────────────────────────────────────── */}
      {showForm && (
        <Modal onClose={() => setShowForm(false)} wide>
          <h2 className="text-lg font-bold text-indigo-900 mb-4 flex items-center gap-2">
            <Ticket size={18} /> Generate New Ticket
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Product Type */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Product Type *</label>
              <SearchableDropdown
                options={productOptions}
                value={form.ProductType}
                onChange={v => setForm({ ...form, ProductType: v })}
                placeholder="Select Product Type"
              />
            </div>

            {/* Tally SL No */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Tally SL No.</label>
              <input
                className="border rounded px-3 py-2 w-full text-sm"
                placeholder="Tally SL No."
                value={form.TallySlNo}
                onChange={e => setForm({ ...form, TallySlNo: e.target.value })}
              />
            </div>

            {/* Customer Name */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Customer Name *</label>
              <input
                className="border rounded px-3 py-2 w-full text-sm"
                placeholder="Customer Name"
                value={form.customerName}
                onChange={e => setForm({ ...form, customerName: e.target.value })}
              />
            </div>

            {/* Phone */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Phone *</label>
              <div className="relative">
                <Phone size={14} className="absolute left-2 top-2.5 text-gray-400" />
                <input
                  className="border rounded pl-7 pr-3 py-2 w-full text-sm"
                  placeholder="10-digit mobile"
                  value={form.contact_no}
                  onChange={e => setForm({ ...form, contact_no: e.target.value })}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Email ID</label>
              <div className="relative">
                <Mail size={14} className="absolute left-2 top-2.5 text-gray-400" />
                <input
                  className="border rounded pl-7 pr-3 py-2 w-full text-sm"
                  placeholder="customer@example.com"
                  value={form.emailId}
                  onChange={e => setForm({ ...form, emailId: e.target.value })}
                />
              </div>
            </div>

            {/* Contact Person */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Contact Person</label>
              <div className="relative">
                <User size={14} className="absolute left-2 top-2.5 text-gray-400" />
                <input
                  className="border rounded pl-7 pr-3 py-2 w-full text-sm"
                  placeholder="Contact Person"
                  value={form.contact_person}
                  onChange={e => setForm({ ...form, contact_person: e.target.value })}
                />
              </div>
            </div>

            {/* Assign To */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Assign To</label>
              <SearchableDropdown
                options={employeeOptions}
                value={form.assigned_to}
                onChange={v => setForm({ ...form, assigned_to: v })}
                placeholder="Select Employee"
              />
            </div>

            {/* Support Type */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Support Type</label>
              <SearchableDropdown
                options={supportOptions}
                value={form.serviceType}
                onChange={v => setForm({ ...form, serviceType: v })}
                placeholder="Select Support Type"
              />
            </div>

            {/* Priority */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Priority</label>
              <SearchableDropdown
                options={priorityOptions}
                value={form.priority}
                onChange={v => setForm({ ...form, priority: v })}
                placeholder="Select Priority"
              />
            </div>

            {/* Created By */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Created By</label>
              <input
                className="border rounded px-3 py-2 w-full text-sm bg-gray-50 text-gray-500"
                readOnly
                value={`${employeeName} (${employeeId})`}
              />
            </div>

          </div>
          <div className="mt-3">
            <label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <AlignLeft size={12} /> Description
            </label>
            <textarea className="border rounded px-3 py-2 w-full text-sm" rows={3} placeholder="Describe the issue…"
              value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="mt-4 w-full bg-indigo-700 hover:bg-indigo-800 text-white py-2.5 rounded-lg font-medium disabled:opacity-60 transition"
          >
            {submitting ? "Submitting…" : "Submit & Send SMS"}
          </button>
        </Modal>
      )}

      {/* ── Close Ticket Modal ────────────────────────────────────────────────── */}
      {closeModal && (
        <Modal onClose={() => setCloseModal(null)}>
          <h2 className="text-lg font-bold text-green-800 mb-1 flex items-center gap-2">
            <CheckCircle size={18} /> Close Ticket
          </h2>
          <p className="text-xs text-gray-400 mb-4">#{closeModal.ticket_id} — {closeModal.customerName}</p>

          <label className="text-xs text-gray-500 mb-1 block">Remarks / Resolution Notes *</label>
          <textarea
            className="border rounded px-3 py-2 w-full text-sm"
            rows={4}
            placeholder="Describe how the issue was resolved…"
            value={closeRemarks}
            onChange={e => setCloseRemarks(e.target.value)}
          />

          <p className="text-xs text-gray-400 mt-2 mb-4">
            📧 A resolution email with a 5-star feedback link will be sent to the customer.
          </p>

          <button
            onClick={handleClose}
            disabled={submitting || !closeRemarks.trim()}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg font-medium disabled:opacity-60 transition"
          >
            {submitting ? "Closing…" : "Mark as Closed & Send Email"}
          </button>
        </Modal>
      )}

      {/* ── View Ticket Modal ─────────────────────────────────────────────────── */}
      {viewTicket && (
        <Modal onClose={() => setViewTicket(null)} wide>
          <h2 className="text-lg font-bold text-indigo-900 mb-1 flex items-center gap-2">
            <Eye size={18} /> Ticket Details
          </h2>
          <p className="text-xs text-indigo-400 font-mono mb-5">{viewTicket.ticket_id}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
            {[
              ["Customer Name", viewTicket.customerName],
              ["Contact No.", viewTicket.contact_no],
              ["Email ID", viewTicket.emailId || "—"],
              ["Contact Person", viewTicket.contact_person || "—"],
              ["Service Type", viewTicket.serviceType || "—"],
              ["Assigned To", viewTicket.assigned_person || "—"],
              ["Created By", viewTicket.created_by_name || viewTicket.created_by],
              ["Priority", viewTicket.priority],
              ["Status", null],

              // ── UPDATED GENERATED DATE ──
              ["Generated Date", viewTicket.generated_date
                ? new Date(viewTicket.generated_date).toLocaleString("en-IN", {
                  timeZone: "Asia/Kolkata",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true
                })
                : "—"],

              // ── UPDATED CLOSED DATE (Using updated_at as per your DB schema) ──
              ["Closed Date", viewTicket.status === "closed" && viewTicket.closed_date
                ? new Date(viewTicket.closed_date).toLocaleString("en-IN", {
                  timeZone: "Asia/Kolkata",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true
                })
                : "—"],

              ["Client Feedback", null],
            ].map(([label, val]) => (
              <div key={label}>
                <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                {label === "Status" ? <Badge status={viewTicket.status} /> :
                  label === "Client Feedback" ? <StarRating value={viewTicket.feedback} /> :
                    <p className="font-medium text-gray-800">{val}</p>}
              </div>
            ))}
          </div>

          {viewTicket.description && (
            <div className="mt-4 border-t pt-3">
              <p className="text-xs text-gray-400 mb-1">Description</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{viewTicket.description}</p>
            </div>
          )}

          {viewTicket.remarks && (
            <div className="mt-3 border-t pt-3">
              <p className="text-xs text-gray-400 mb-1">Closing Remarks</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{viewTicket.remarks}</p>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}